<template>
  <v-dialog
    v-model="surveyModel"
    persistent
    max-width="600"
    content-class="survey-dialog"
  >
    <div class="survey">
      <!-- Header -->
      <div class="survey__header">
        <div class="survey__badge">
          <v-icon color="#ffffff" size="22">mdi-message-text-outline</v-icon>
        </div>
        <div class="survey__heading">
          <p class="survey__eyebrow">We value your feedback</p>
          <h2 class="survey__title">{{ surveyDetails.title }}</h2>
        </div>
      </div>

      <img
        v-if="attachmentUrl"
        :src="attachmentUrl"
        class="survey__banner"
        alt=""
      />

      <!-- Progress -->
      <div class="survey__progress" v-show="surveyQuestions.length">
        <div class="survey__track">
          <div class="survey__fill" :style="{ width: progressValue + '%' }"></div>
        </div>
        <div class="survey__progress-meta">
          <span>{{ progressLabel }}</span>
          <span>{{ progressPercent }}%</span>
        </div>
      </div>

      <div class="survey__body">
        <!-- Page 1: intro -->
        <div class="survey__intro" v-show="currentPage == 1">
          <!-- Admin-configured memorandum (WYSIWYG). Falls back to the default
               welcome message when a survey has none. -->
          <div
            v-if="surveyDetails.memorandum"
            class="survey__memo"
            v-html="surveyDetails.memorandum"
          ></div>
          <template v-else>
            <p>
              🎉 It's been nearly one year since the launch of the HRDC. As part
              of our commitment to continuously improve, we're reaching out to
              gather your feedback during this period. 🎉
            </p>
            <p>Please take a moment to share your thoughts with us. 😊</p>
            <p>Thanks for being part of this journey! 🙏</p>
            <div class="survey__sign"><i>– taxPOD Product Team</i></div>
          </template>
        </div>

        <!-- Question pages -->
        <div
          class="survey__question"
          v-for="(item, key) in surveyQuestions"
          :key="key"
          v-show="currentPage == key + 2"
        >
          <p class="survey__q-eyebrow">Question {{ key + 1 }}</p>
          <h3 class="survey__q-title">{{ item.question }}</h3>

          <!-- Radio -->
          <div
            v-if="item.answerType == 'radio'"
            class="survey__options"
            role="radiogroup"
          >
            <button
              v-for="(option, optKey) in item.SurveyOption"
              :key="optKey"
              type="button"
              role="radio"
              :aria-checked="surveyAnswer[item.uuid] === option.option"
              class="opt"
              :class="{ 'opt--active': surveyAnswer[item.uuid] === option.option }"
              @click="selectRadio(item, option.option)"
            >
              <span class="opt__marker opt__marker--round">
                <v-icon
                  v-if="surveyAnswer[item.uuid] === option.option"
                  size="14"
                  color="#ffffff"
                  >mdi-check</v-icon
                >
              </span>
              <span class="opt__label">{{ option.option }}</span>
            </button>
          </div>

          <!-- Checkbox -->
          <div
            v-if="item.answerType == 'checkbox'"
            class="survey__options"
            role="group"
          >
            <button
              v-for="(option, optKey) in item.SurveyOption"
              :key="optKey"
              type="button"
              role="checkbox"
              :aria-checked="isChecked(item, option.option)"
              class="opt"
              :class="{ 'opt--active': isChecked(item, option.option) }"
              @click="toggleCheckbox(item, option.option)"
            >
              <span class="opt__marker opt__marker--square">
                <v-icon
                  v-if="isChecked(item, option.option)"
                  size="14"
                  color="#ffffff"
                  >mdi-check</v-icon
                >
              </span>
              <span class="opt__label">{{ option.option }}</span>
            </button>
          </div>

          <!-- Textarea -->
          <div v-if="item.answerType == 'textarea'">
            <textarea
              v-model="surveyAnswer[item.uuid]"
              class="survey__textarea"
              :class="{ 'survey__textarea--error': errorField[item.uuid] }"
              rows="4"
              placeholder="Type your answer here…"
              @input="trackQuestion(item.uuid)"
            ></textarea>
          </div>

          <!-- Inline validation message -->
          <p v-if="errorField[item.uuid]" class="survey__error">
            <v-icon size="16" color="#dc2626">mdi-alert-circle-outline</v-icon>
            {{ errorField[item.uuid] }}
          </p>
        </div>

        <!-- Thank you -->
        <div class="survey__thanks" v-show="isThankYouPage">
          <div class="survey__thanks-icon">
            <v-icon color="#16a34a" size="44">mdi-check</v-icon>
          </div>
          <h3 class="survey__thanks-title">Thank you!</h3>
          <p class="survey__thanks-sub">
            Your feedback has been recorded and helps us make HRDC better. 🙏
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="survey__footer">
        <button
          v-if="currentPage > 1 && !isThankYouPage"
          type="button"
          class="survey-btn survey-btn--secondary"
          @click="togglePage(false)"
        >
          <v-icon size="16" class="mr-1">mdi-chevron-left</v-icon>
          Back
        </button>
        <v-spacer></v-spacer>
        <button
          v-if="!isLastPage && !isThankYouPage"
          type="button"
          class="survey-btn survey-btn--primary"
          @click="togglePage(true)"
        >
          Next
          <v-icon size="16" class="ml-1">mdi-chevron-right</v-icon>
        </button>
        <button
          v-if="isLastPage"
          type="button"
          class="survey-btn survey-btn--primary"
          @click="submitFormChildren()"
        >
          <v-icon size="16" class="mr-1">mdi-send</v-icon>
          Submit
        </button>
        <button
          v-if="isThankYouPage"
          type="button"
          class="survey-btn survey-btn--primary"
          @click="toggleSurveyDialog(false)"
        >
          Close
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: "Survey",
  props: {
    surveyModel: Boolean,
    surveyQuestions: Array,
    surveyDetails: Object,
    surveyAnswer: Object,
    errorField: Object,
  },
  emits: ["toggleSurveyDialogChildren", "submitFormChildren"],
  computed: {
    isLastPage() {
      return this.surveyQuestions.length == this.currentPage - 1;
    },
    isThankYouPage() {
      return this.currentPage - 2 == this.surveyQuestions.length;
    },
    // Drives the progress bar (0–100). Intro is 0%, the thank-you page is 100%,
    // and each question advances it evenly in between.
    progressValue() {
      const total = this.surveyQuestions.length;
      if (!total) return this.isThankYouPage ? 100 : 0;
      if (this.isThankYouPage) return 100;
      if (this.currentPage <= 1) return 0;
      return ((this.currentPage - 1) / total) * 100;
    },
    progressPercent() {
      return Math.round(this.progressValue);
    },
    // Human-readable step indicator shown under the progress bar.
    progressLabel() {
      if (this.isThankYouPage) return "Completed";
      if (this.currentPage <= 1) return "Introduction";
      return `Question ${this.currentPage - 1} of ${this.surveyQuestions.length}`;
    },
    // Banner image: served via the surveyUuid-scoped public view endpoint
    // (never an arbitrary blob path). Null hides the image when none is set.
    attachmentUrl() {
      if (
        !this.surveyDetails ||
        !this.surveyDetails.attachment ||
        !this.surveyDetails.uuid
      )
        return null;
      return `${this.$service.hrdc}/v1/en/console/survey/view-survey-attachment?surveyUuid=${encodeURIComponent(
        this.surveyDetails.uuid
      )}`;
    },
  },
  data: () => ({
    currentPage: 1,
  }),
  methods: {
    toggleSurveyDialog(val) {
      this.$emit("toggleSurveyDialogChildren", val);
    },
    // Radio: single value. Stored as the option string (matches submit shape).
    selectRadio(item, value) {
      this.$set(this.surveyAnswer, item.uuid, value);
      this.trackQuestion(item.uuid);
    },
    // Checkbox: multi value. Stored as an array of selected option strings.
    toggleCheckbox(item, value) {
      const current = Array.isArray(this.surveyAnswer[item.uuid])
        ? this.surveyAnswer[item.uuid].slice()
        : [];
      const idx = current.indexOf(value);
      if (idx === -1) {
        current.push(value);
      } else {
        current.splice(idx, 1);
      }
      this.$set(this.surveyAnswer, item.uuid, current);
      this.trackQuestion(item.uuid);
    },
    isChecked(item, value) {
      const answer = this.surveyAnswer[item.uuid];
      return Array.isArray(answer) && answer.includes(value);
    },
    togglePage(toggle) {
      if (toggle) {
        // Check for answer filled before proceed next page (only check after page 1).
        if (this.currentPage !== 1) {
          const questionIndex = Object.keys(this.surveyAnswer)[
            this.currentPage - 2
          ];
          const isValidate = this.validateInput(questionIndex);
          if (!isValidate) {
            this.$set(this.errorField, questionIndex, "This field is required");
            return false;
          }
        }
        this.currentPage++;
      }

      if (!toggle) {
        this.currentPage--;
      }
    },
    validateInput(questionIndex) {
      const question = this.surveyQuestions.find(
        (item) => item.uuid == questionIndex
      );
      const { answerType } = question;
      const answer = this.surveyAnswer[questionIndex];

      switch (answerType) {
        case "textarea":
        case "radio":
          if (!answer) {
            return false;
          }
          break;

        case "checkbox":
          if (!answer || answer.length == 0) {
            return false;
          }
          break;
      }

      return true;
    },
    trackQuestion(questionUuid) {
      const answer = this.surveyAnswer[questionUuid];

      const question = this.surveyQuestions.find(
        (item) => item.uuid == questionUuid
      );
      const { answerType } = question;

      switch (answerType) {
        default:
        case "textarea":
        case "radio":
          if (answer) {
            this.clearErrorMsg(questionUuid);
          }
          break;

        case "checkbox":
          if (answer.length > 0) {
            this.clearErrorMsg(questionUuid);
          }
          break;
      }
    },
    submitFormChildren() {
      // Before submit check for 1 last time.
      for (const question of this.surveyQuestions) {
        const val = this.validateInput(question.uuid);
        if (!val) {
          this.$set(this.errorField, question.uuid, "This field is required");
          return false;
        }
      }

      this.$emit("submitFormChildren", this.surveyAnswer);

      // Show thank you page.
      this.currentPage++;
    },
  },
};
</script>

<style>
/* Dialog wrapper (lives outside this component's scope — global, namespaced). */
.v-dialog.survey-dialog {
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(2, 6, 23, 0.4);
}
</style>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .survey {
  background: var(--app-card);
  color: var(--app-text);
}

.dark-mode .survey__header {
  background: var(--app-brand-gradient);
}

.dark-mode .survey__track {
  background: var(--app-elevated);
}

.dark-mode .survey__fill {
  background: linear-gradient(90deg, var(--app-accent), var(--app-accent));
}

.dark-mode .survey__progress-meta {
  color: var(--app-text-muted);
}

.dark-mode .survey__body::-webkit-scrollbar-thumb {
  background: var(--app-elevated);
}

.dark-mode .survey__intro {
  color: var(--app-text);
}

.dark-mode .survey__sign {
  color: var(--app-accent);
}

.dark-mode .survey__memo {
  color: var(--app-text);
}

.dark-mode .survey__q-eyebrow {
  color: var(--app-accent);
}

.dark-mode .survey__q-title {
  color: var(--app-text);
}

.dark-mode .opt {
  background: var(--app-card);
  border: 1.5px solid var(--app-border);
}

.dark-mode .opt:hover {
  border-color: var(--app-border-strong);
  background: var(--app-surface);
}

.dark-mode .opt--active {
  border-color: var(--app-accent);
}

.dark-mode .opt__marker {
  border: 2px solid var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .opt--active .opt__marker {
  border-color: var(--app-accent);
  background: var(--app-accent);
}

.dark-mode .opt__label {
  color: var(--app-text);
}

.dark-mode .opt--active .opt__label {
  color: var(--app-accent);
}

.dark-mode .survey__textarea {
  background: var(--app-card);
  border: 1.5px solid var(--app-border);
  color: var(--app-text);
}

.dark-mode .survey__textarea::placeholder {
  color: var(--app-text-faint);
}

.dark-mode .survey__textarea:focus {
  border-color: var(--app-accent);
}

.dark-mode .survey__thanks-title {
  color: var(--app-text);
}

.dark-mode .survey__thanks-sub {
  color: var(--app-text-muted);
}

.dark-mode .survey__footer {
  border-top: 1px solid var(--app-border);
}

.dark-mode .survey-btn--primary {
  background: var(--app-brand-gradient);
}

.dark-mode .survey-btn--secondary {
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-text);
}

.dark-mode .survey-btn--secondary:hover {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
}

/* >>> end auto-dark <<< */

.survey {
  background: #ffffff;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Helvetica, Arial, sans-serif;
  color: #1e293b;
  /* Flex column capped to the dialog height so the footer (Next/Submit) stays
     pinned and only the body scrolls — otherwise tall questions push the footer
     past the dialog's clipped (overflow:hidden) edge and it can't be reached. */
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

/* Header ------------------------------------------------------------------ */
.survey__header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 28px;
  background: linear-gradient(135deg, #063058 0%, #0a4f8c 100%);
}
.survey__badge {
  flex: none;
  width: 46px;
  height: 46px;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
}
.survey__eyebrow {
  margin: 0 0 3px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}
.survey__title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1.3;
  color: #ffffff;
  word-break: break-word;
}

/* Banner ------------------------------------------------------------------ */
.survey__banner {
  flex: none;
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  /* Cap the banner so it never crowds out the body + footer on short screens. */
  max-height: 30vh;
  object-fit: cover;
}

/* Progress ---------------------------------------------------------------- */
.survey__progress {
  flex: none;
  padding: 20px 28px 4px;
}
.survey__track {
  height: 8px;
  background: #eef2f7;
  border-radius: 9999px;
  overflow: hidden;
}
.survey__fill {
  height: 100%;
  border-radius: 9999px;
  background: linear-gradient(90deg, #063058, #1f8fd6);
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.survey__progress-meta {
  display: flex;
  justify-content: space-between;
  margin-top: 9px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
}

/* Body -------------------------------------------------------------------- */
.survey__body {
  /* Grows to fill the space between the pinned header/banner/progress and the
     footer; min-height:0 lets it actually shrink-and-scroll inside the flex
     column instead of overflowing the dialog. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.survey__body::-webkit-scrollbar {
  width: 8px;
}
.survey__body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 9999px;
}
.survey__body::-webkit-scrollbar-track {
  background: transparent;
}

/* Intro ------------------------------------------------------------------- */
.survey__intro {
  padding: 28px 30px 14px;
  text-align: center;
  font-size: 1.02rem;
  line-height: 1.8;
  color: #334155;
}
.survey__intro p {
  margin-bottom: 14px;
}
.survey__sign {
  margin-top: 18px;
  text-align: right;
  font-weight: 600;
  color: #063058;
}
.survey__memo {
  text-align: left;
  color: #334155;
  line-height: 1.7;
}
.survey__memo ::v-deep p {
  margin-bottom: 12px;
}
.survey__memo ::v-deep img {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
}

/* Question ---------------------------------------------------------------- */
.survey__question {
  padding: 28px 30px 10px;
}
.survey__q-eyebrow {
  margin: 0 0 8px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #063058;
}
.survey__q-title {
  margin: 0 0 22px;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.45;
  color: #1e293b;
}

/* Options as selectable cards --------------------------------------------- */
.survey__options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.opt {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 16px;
  text-align: left;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 0.18s ease, background-color 0.18s ease,
    box-shadow 0.18s ease;
  font-family: inherit;
}
.opt:hover {
  border-color: #94a3b8;
  background: #f8fafc;
}
.opt--active {
  border-color: #063058;
  background: rgba(6, 48, 88, 0.05);
  box-shadow: inset 0 0 0 1px #063058;
}
.opt__marker {
  flex: none;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #cbd5e1;
  background: #ffffff;
  transition: border-color 0.18s ease, background-color 0.18s ease;
}
.opt__marker--round {
  border-radius: 9999px;
}
.opt__marker--square {
  border-radius: 7px;
}
.opt--active .opt__marker {
  border-color: #063058;
  background: #063058;
}
.opt__label {
  flex: 1;
  font-size: 0.97rem;
  line-height: 1.4;
  color: #334155;
}
.opt--active .opt__label {
  color: #063058;
  font-weight: 600;
}

/* Textarea ---------------------------------------------------------------- */
.survey__textarea {
  width: 100%;
  min-height: 120px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  font-family: inherit;
  font-size: 0.97rem;
  line-height: 1.6;
  color: #1e293b;
  resize: vertical;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.survey__textarea::placeholder {
  color: #94a3b8;
}
.survey__textarea:focus {
  outline: none;
  border-color: #063058;
  box-shadow: 0 0 0 3px rgba(6, 48, 88, 0.12);
}
.survey__textarea--error {
  border-color: #dc2626;
}

/* Inline error ------------------------------------------------------------ */
.survey__error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 12px 0 0;
  font-size: 0.85rem;
  font-weight: 500;
  color: #dc2626;
}

/* Thank you --------------------------------------------------------------- */
.survey__thanks {
  padding: 48px 30px;
  text-align: center;
}
.survey__thanks-icon {
  width: 88px;
  height: 88px;
  margin: 0 auto 20px;
  border-radius: 9999px;
  background: rgba(22, 163, 74, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.survey__thanks-title {
  margin: 0 0 8px;
  font-size: 1.4rem;
  font-weight: 700;
  color: #1e293b;
}
.survey__thanks-sub {
  margin: 0 auto;
  max-width: 360px;
  font-size: 0.98rem;
  line-height: 1.6;
  color: #64748b;
}

/* Footer ------------------------------------------------------------------ */
.survey__footer {
  flex: none;
  display: flex;
  align-items: center;
  padding: 18px 24px;
  border-top: 1px solid #f1f5f9;
}

/* Buttons — mirrors the HRDC "New Application" button style ---------------- */
.survey-btn {
  display: inline-flex;
  align-items: center;
  font-family: "Lato", sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
}
.survey-btn:focus {
  outline: none;
}
/* Icons follow the button's text color in both light and dark modes. */
.survey-btn ::v-deep .v-icon {
  color: currentColor !important;
}
.survey-btn--primary {
  padding: 8px 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #063058 0%, #0a4f8c 100%);
  color: #ffffff;
}
.survey-btn--primary:hover {
  background: linear-gradient(135deg, #07385f 0%, #0b5ba0 100%);
  box-shadow: 0 4px 12px rgba(6, 48, 88, 0.4);
}
.survey-btn--secondary {
  padding: 7px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
}
.survey-btn--secondary:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

/* Dark mode --------------------------------------------------------------- */
.theme--dark .survey {
  background: var(--app-card);
  color: var(--app-text);
}
.theme--dark .survey__track {
  background: var(--app-border);
}
.theme--dark .survey__intro,
.theme--dark .survey__memo {
  color: var(--app-neutral-fg);
}
.theme--dark .survey__sign {
  color: var(--app-accent);
}
.theme--dark .survey__q-title,
.theme--dark .survey__thanks-title {
  color: var(--app-text);
}
.theme--dark .opt {
  background: var(--app-page);
  border-color: var(--app-border);
}
.theme--dark .opt:hover {
  background: var(--app-card);
  border-color: var(--app-border-strong);
}
.theme--dark .opt--active {
  background: var(--app-accent-soft);
  border-color: var(--app-accent);
  box-shadow: inset 0 0 0 1px var(--app-accent);
}
.theme--dark .opt__marker {
  background: var(--app-page);
  border-color: var(--app-border-strong);
}
.theme--dark .opt--active .opt__marker {
  background: var(--app-accent);
  border-color: var(--app-accent);
}
.theme--dark .opt__label {
  color: var(--app-neutral-fg);
}
.theme--dark .opt--active .opt__label {
  color: var(--app-accent-strong);
}
.theme--dark .survey__textarea {
  background: var(--app-page);
  border-color: var(--app-border);
  color: var(--app-text);
}
.theme--dark .survey__footer {
  border-color: var(--app-border);
}
.theme--dark .survey-btn--secondary {
  background: var(--app-page);
  border-color: var(--app-border);
  color: var(--app-neutral-fg);
}
.theme--dark .survey-btn--secondary:hover {
  background: var(--app-card);
  border-color: var(--app-border-strong);
}
.theme--dark .survey-btn--primary {
  background: var(--app-brand-gradient);
  color: var(--app-brand-gradient-fg);
}
.theme--dark .survey-btn--primary:hover {
  background: var(--app-brand-gradient-hover);
  box-shadow: 0 4px 14px var(--app-brand-glow);
}
</style>
